/***************************************************************************
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import { LitElement } from 'lit';
import { uniqueId } from 'lodash-es';
/**
 * A3D tags used in the viewer.
 */
export const A3D_TAGS = {
    VIEWER: 'a3d-viewer',
    ENVIRONMENT: 'a3d-environment',
    CAMERA: 'a3d-camera',
    NODE: 'a3d-node',
    MODEL: 'a3d-model',
    HOTSPOT: 'a3d-hot-spot',
};
/**
 * A3D tag names used in the viewer.
 */
export const A3D_TAG_NAMES = {
    VIEWER: A3D_TAGS.VIEWER.toUpperCase(),
    ENVIRONMENT: A3D_TAGS.ENVIRONMENT.toUpperCase(),
    CAMERA: A3D_TAGS.CAMERA.toUpperCase(),
    NODE: A3D_TAGS.NODE.toUpperCase(),
    MODEL: A3D_TAGS.MODEL.toUpperCase(),
    HOTSPOT: A3D_TAGS.HOTSPOT.toUpperCase(),
};
export var A3dElementErrorNames;
(function (A3dElementErrorNames) {
    A3dElementErrorNames["PARENT_WARNING"] = "A3dElementParentWarning";
})(A3dElementErrorNames || (A3dElementErrorNames = {}));
export const A3dElementErrorMessages = {
    [A3dElementErrorNames.PARENT_WARNING]: `Should be a child of ${A3D_TAGS.VIEWER}`,
};
/**
 * Converter for lit element from string to an array of three numbers.
 * @param value The input attribute value to convert.
 * @param errorMessage The error message to display if the conversion fails.
 * @returns An array of three numbers or null if the conversion fails.
 */
export function toVectorArray(value, errorMessage) {
    if (!value)
        return null;
    const numbers = value.split(/\s+/).map(Number);
    if (numbers.length !== 3) {
        console.error(errorMessage);
        return null;
    }
    else {
        return [numbers[0], numbers[1], numbers[2]];
    }
}
// Type guard checks if an element is a A3dElement instance based on its constructor
export function isA3dElementInstance(el) {
    return (typeof el.constructor === 'function' &&
        el.constructor.isA3dElement === true);
}
/**
 * Parent class for a3d elements.
 * Every a3d element will receive a viewer, a scene and a viewerDetails from a `a3d-viewer` parent element.
 */
export class A3dElement extends LitElement {
    // Static marker used to identify this class as compatible with A3dElement initialization logic
    static get isA3dElement() {
        return true;
    }
    constructor() {
        super();
        this._details = null;
        /**
         * Internal ID used if no `id` attribute is provided.
         * @internal
         */
        this._internalId = uniqueId(`${this.constructor.name}-`);
        this._details = null;
    }
    connectedCallback() {
        super.connectedCallback();
        this.id || (this.id = this._internalId);
        this._validateParent();
    }
    /**
     * Initializes the element with viewer details.
     * @param viewerDetails The viewer details.
     * @internal
     */
    async init(viewerDetails) {
        if (this._details) {
            this._warn(`Init has already been called`);
            return;
        }
        this._details = viewerDetails;
        await this._initElement();
    }
    /**
     * Validates if the parent element is correct.
     * @returns {boolean} True if the parent is invalid, false otherwise.
     */
    _validateParent() {
        if (this.parentElement?.tagName === A3D_TAG_NAMES.VIEWER) {
            return true;
        }
        this._warn(A3dElementErrorMessages[A3dElementErrorNames.PARENT_WARNING]);
        return false;
    }
    /**
     * Dispatches an error event.
     * @internal
     */
    _dispatchErrorEvent(name, message) {
        console.error(`[${this.constructor.name}] ${message}`);
        this.dispatchEvent(new CustomEvent('error', {
            detail: { name, message },
            bubbles: true,
            composed: true,
            cancelable: true,
        }));
    }
    /**
     * Logs a warning message to the console with the class name as a prefix.
     * @param message - The warning message to be logged.
     */
    _warn(message) {
        console.warn(`[${this.constructor.name}] ${message}`);
    }
    /**
     * Returns the viewer instance if available.
     */
    get _viewer() {
        return this._details?.viewer ?? null;
    }
    /**
     * Returns the scene instance if available.
     */
    get _scene() {
        return this._details?.scene ?? null;
    }
}
